// A09 — Engineer application approval queue.
//
// Each row is read-only summary; clicking "Review" opens the detail page
// (ApprovalDetail.tsx) where the admin can actually see the photo ID +
// IT certs before deciding. Cap. 115 identity verification requires the
// reviewer to look at the document — no more inline rubber-stamp Approve.

import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { formatDateHK } from "@findgeeks/shared";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface EngineerApplicationRow {
  user_id: string;
  qualification_level: string | null;
  approval_status: string;
  group_name: string | null;
  created_at: string;
  user_profiles: {
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  };
}

const QUAL_LABEL: Record<string, string> = {
  primary: "Primary Engineer",
  senior: "Senior Engineer",
  expert: "Expert Engineer",
};

export function ApprovalQueue() {
  const navigate = useNavigate();
  const list = usePagedQuery<EngineerApplicationRow>("engineer-applications", {
    filters: { status: "pending" },
  });

  const fullName = (r: EngineerApplicationRow): string => {
    const p = r.user_profiles;
    return (
      [p.first_name, p.last_name].filter(Boolean).join(" ") ||
      p.display_name ||
      p.email ||
      "—"
    );
  };

  const columns: DataTableColumn<EngineerApplicationRow>[] = [
    {
      field: "display_name",
      headerName: "User Name",
      render: (r) => fullName(r),
    },
    {
      field: "qualification_level",
      headerName: "Role",
      render: (r) => QUAL_LABEL[r.qualification_level ?? ""] ?? "Engineer",
    },
    {
      field: "email",
      headerName: "E-mail",
      render: (r) => r.user_profiles.email ?? "-",
    },
    {
      field: "phone",
      headerName: "Phone",
      render: (r) => r.user_profiles.phone ?? "-",
    },
    {
      field: "created_at",
      headerName: "Submitted",
      render: (r) => formatDateHK(r.created_at),
    },
  ];

  const reviewBtn = {
    px: 2.5,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
    backgroundColor: tokens.colors.admin.primary,
    "&:hover": {
      backgroundColor: tokens.colors.admin.primaryHover,
      boxShadow: "none",
    },
  };

  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
  } as const;

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography sx={sectionHeading}>New app account application</Typography>
        </Box>
        <Box sx={{ px: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowCount={list.data?.total ?? 0}
            paginationModel={list.paginationModel}
            onPaginationModelChange={list.setPaginationModel}
            getRowId={(r) => r.user_id}
            loading={list.isLoading}
            bordered={false}
            rowActions={(row) => (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  sx={reviewBtn}
                  onClick={() =>
                    navigate(`/app/app-users/approval/${row.user_id}`)
                  }
                >
                  Review
                </Button>
              </Stack>
            )}
          />
        </Box>
      </Paper>
    </Box>
  );
}
